import { useNavigate } from 'react-router-dom'
import { Box, Typography, Button, Collapse } from '@mui/material'
import { usePushNotifications } from '../contexts/PushNotificationContext'

const colourToSeverity: Record<string, { bg: string; border: string }> = {
  info: { bg: 'info.dark', border: 'info.main' },
  warning: { bg: 'warning.dark', border: 'warning.main' },
  error: { bg: 'error.dark', border: 'error.main' },
}

function getStyles(colour: string) {
  // Treat empty or legacy 'default' as info
  const c = colour === 'default' || !colour ? 'info' : colour
  return colourToSeverity[c] || colourToSeverity.info
}

// Banner fills are always *.dark, so labels and actions stay light. Theme
// outlined buttons use --vs-on-outline (navy in light mode) and disappear here.
const bannerOnColor = '#fff'
const bannerPrimaryButtonSx = {
  fontWeight: 600,
  bgcolor: bannerOnColor,
  color: '#001123',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: 'rgba(255, 255, 255, 0.9)',
    color: '#001123',
    boxShadow: 'none',
  },
}
const bannerSecondaryButtonSx = {
  fontWeight: 600,
  color: bannerOnColor,
  borderColor: 'rgba(255, 255, 255, 0.78)',
  '&:hover': {
    borderColor: bannerOnColor,
    bgcolor: 'rgba(255, 255, 255, 0.16)',
    color: bannerOnColor,
  },
}

export default function PushNotificationBanner() {
  const navigate = useNavigate()
  const { notifications, acknowledge } = usePushNotifications()
  const current = notifications[0] ?? null

  if (!current) return null

  const styles = getStyles(current.colour)

  return (
    <Collapse in>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: styles.border,
          bgcolor: styles.bg,
          color: bannerOnColor,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} color="inherit">
            {current.title}
          </Typography>
          {current.description && (
            <Typography variant="body2" color="inherit" sx={{ opacity: 0.95 }}>
              {current.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          {current.link && (
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                acknowledge(current.id)
                navigate(current.link!)
              }}
              sx={bannerPrimaryButtonSx}
            >
              {current.link_label || 'Go to Billing'}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={() => acknowledge(current.id)}
            sx={bannerSecondaryButtonSx}
          >
            Acknowledge
          </Button>
        </Box>
      </Box>
    </Collapse>
  )
}
